import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, XCircle, Clock, ArrowRight, User, FileText } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { ReferralConsentModal } from './ReferralConsentModal';

export const ReferralsList = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { get } = useApi();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [selectedReferral, setSelectedReferral] = useState(null);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        fetchReferrals();
    }, []);

    const fetchReferrals = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = user.role === 'counselor' ? `?type=${activeTab}` : '';
            const response = await get(`/referrals${params}`);

            if (user.role === 'counselor' && activeTab === 'all') {
                // Combine incoming and outgoing
                const data = response.data.data;
                setReferrals([...(data.incoming || []), ...(data.outgoing || [])]);
            } else {
                setReferrals(response.data.data || []);
            }
        } catch (err) {
            setError('Failed to load referrals');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending_student_consent: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Consent' },
            student_approved: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Awaiting Counselor' },
            student_declined: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Declined by Student' },
            counselor_accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Accepted' },
            counselor_declined: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Declined by Counselor' },
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
            cancelled: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Cancelled' }
        };

        const badge = badges[status] || badges.pending_student_consent;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="h-3 w-3" />
                {badge.label}
            </span>
        );
    };

    const getPriorityBadge = (priority) => {
        if (priority === 'urgent') {
            return <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">Urgent</span>;
        }
        if (priority === 'high') {
            return <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded">High</span>;
        }
        return null;
    };

    const handleReferralClick = (referral) => {
        if (user.role === 'student' && referral.status === 'pending_student_consent') {
            setSelectedReferral(referral);
        } else {
            navigate(`/${user.role}/referrals/${referral._id}`);
        }
    };

    const handleConsentSuccess = () => {
        setSelectedReferral(null);
        fetchReferrals();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Referrals</h1>
                <p className="text-gray-600 mt-1">
                    {user.role === 'student' && 'View and manage your counselor referrals'}
                    {user.role === 'counselor' && 'Manage incoming and outgoing referrals'}
                    {['admin', 'hod'].includes(user.role) && 'View all referrals in the system'}
                </p>
            </div>

            {/* Tabs for Counselors */}
            {user.role === 'counselor' && (
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {['all', 'incoming', 'outgoing'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setTimeout(fetchReferrals, 0);
                                }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p className="ml-3 text-sm text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Referrals List */}
            {referrals.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No referrals found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {referrals.map((referral) => (
                        <div
                            key={referral._id}
                            onClick={() => handleReferralClick(referral)}
                            className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Status and Priority */}
                                    <div className="flex items-center gap-2 mb-2">
                                        {getStatusBadge(referral.status)}
                                        {getPriorityBadge(referral.priority)}
                                    </div>

                                    {/* Participants */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                                        {user.role !== 'student' && (
                                            <div>
                                                <p className="text-xs text-gray-600">Student</p>
                                                <p className="font-medium text-gray-900">{referral.client?.fullName}</p>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-gray-600">From</p>
                                            <p className="font-medium text-gray-900">{referral.referringCounselor?.fullName}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-600">To</p>
                                            <p className="font-medium text-gray-900">{referral.targetCounselor?.fullName}</p>
                                            {referral.targetCounselor?.staffInfo?.specializations && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {referral.targetCounselor.staffInfo.specializations.slice(0, 2).map((spec, idx) => (
                                                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                                            {spec.replace(/_/g, ' ')}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Reason */}
                                    <div>
                                        <p className="text-xs text-gray-600 mb-1">Reason</p>
                                        <p className="text-sm text-gray-700 line-clamp-2">{referral.reason}</p>
                                    </div>

                                    {/* Date */}
                                    <p className="text-xs text-gray-500 mt-2">
                                        Created {new Date(referral.createdAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>

                                {/* Action Indicator */}
                                <div className="ml-4">
                                    {user.role === 'student' && referral.status === 'pending_student_consent' && (
                                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                            Review
                                        </button>
                                    )}
                                    {user.role === 'counselor' && referral.status === 'student_approved' && referral.targetCounselor._id === user._id && (
                                        <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                                            Respond
                                        </button>
                                    )}
                                    {!['pending_student_consent', 'student_approved'].includes(referral.status) && (
                                        <ArrowRight className="h-5 w-5 text-gray-400" />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Consent Modal */}
            {selectedReferral && (
                <ReferralConsentModal
                    referral={selectedReferral}
                    onClose={() => setSelectedReferral(null)}
                    onSuccess={handleConsentSuccess}
                />
            )}
        </div>
    );
};

export default ReferralsList;
